import { rootActions } from "../../reducers/root.slice";
import { call, put, race, SagaGenerator, select, take } from "typed-redux-saga";
import { selectRootState } from "../../selectors";
import { generatePCACacheKey } from "../../utils/utils";
import RowUtils from "../../utils/RowUtils";
import { TPCA } from "../../types";
import workerChannel from "./worker.channel";
import { workerActions } from "../../reducers/worker.slice";
import { TRootState } from "../../store/create-store";
import { selectPCAForCacheKey } from "../../selectors/worker.selector";

type TPCAWorkerAction =
  | ReturnType<typeof workerActions.calculatePCA>
  | ReturnType<typeof rootActions.projectToPCA>;

class WorkerQueue {
  private queue: TPCAWorkerAction[] = [];

  public push(action: TPCAWorkerAction) {
    this.queue.push(action);
  }

  public pop(): TPCAWorkerAction {
    const item = this.queue.pop();
    if (item) {
      return item;
    }

    throw new Error("Queue is empty");
  }

  public get length() {
    return this.queue.length;
  }

  clear() {
    this.queue = [];
  }
}

function* processMessage(channelMessage: unknown) {
  if ((channelMessage as any).type === "computePercentageExplained") {
    const data = channelMessage as {
      key: string;
      pca: TPCA;
    };

    yield* put(workerActions.setPCA(data));
  } else if ((channelMessage as any).type === "computeAdjustedData") {
    const data = channelMessage as {
      key: string;
      adjusted: {
        adjustedData: number[][];
      };
    };

    yield* put(
      workerActions.setProjectToPCA({
        key: data.key,
        adjustedData: data.adjusted.adjustedData,
      })
    );

    yield* put(rootActions.projectToPCADone());
  }
}

function* pcaWorkerSaga() {
  const webWorker = new Worker(
    new URL("../workers/pca.worker.js", import.meta.url)
  );
  const channel = workerChannel(webWorker);

  let isWorkerBusy = false;
  const workerQueue = new WorkerQueue();

  function* processCalculateAction(action: TPCAWorkerAction) {
    if (workerActions.calculatePCA.match(action)) {
      const rootState = yield* select(selectRootState);
      const { rows, leftPanel } = rootState;

      if (leftPanel.selectedDataset !== action.payload.selectedDataset) {
        throw new Error(
          "processCalculatePCAAction: Selected dataset is not the same"
        );
      }

      const cacheKey = generatePCACacheKey(
        action.payload.selectedDataset,
        action.payload.selectedMethod
      );

      const points = RowUtils.getPointsForMethod(
        rows,
        action.payload.selectedMethod
      );

      webWorker.postMessage({
        points,
        type: "computePercentageExplained",
        key: cacheKey,
      });
    } else if (rootActions.projectToPCA.match(action)) {
      const rootState = yield* select(selectRootState);
      const { rows, leftPanel } = rootState;

      if (!leftPanel.selectedDataset || !leftPanel.selectedMethod) {
        throw new Error("No method or column selected");
      }
      const cacheKey = generatePCACacheKey(
        leftPanel.selectedDataset,
        leftPanel.selectedMethod
      );
      const pcaForMethod = yield* select((state: TRootState) =>
        selectPCAForCacheKey(state, cacheKey)
      );

      if (pcaForMethod) {
        const points = RowUtils.getPointsForMethod(
          rows,
          leftPanel.selectedMethod
        );

        const vectors = [
          {
            eigenvalue: pcaForMethod.x,
            vector: pcaForMethod.eigenVectors[0],
          },
          {
            eigenvalue: pcaForMethod.y,
            vector: pcaForMethod.eigenVectors[1],
          },
          {
            eigenvalue: pcaForMethod.z,
            vector: pcaForMethod.eigenVectors[2],
          },
        ];

        webWorker.postMessage({
          points,
          eigenVectors: vectors,
          type: "computeAdjustedData",
          key: cacheKey,
        });
      } else {
        throw new Error("No PCA found");
      }
    }
  }

  while (true) {
    try {
      const raceResult = yield* race({
        calculatePCAAction: take(workerActions.calculatePCA),
        projectToPCA: take(rootActions.projectToPCA),
        channelMessage: take(channel),
        initialise: take(rootActions.initialise),
        newDataset: take(rootActions.setSelectedDataset),
      });

      const datasetReset = raceResult.newDataset || raceResult.initialise;
      const calculationAction: TPCAWorkerAction | undefined =
        raceResult.calculatePCAAction || raceResult.projectToPCA;

      if (datasetReset) {
        workerQueue.clear();
      } else if (calculationAction) {
        if (isWorkerBusy) {
          workerQueue.push(calculationAction);
        } else {
          isWorkerBusy = true;
          yield* call(processCalculateAction, calculationAction);
        }
      } else if (raceResult.channelMessage) {
        yield* call(processMessage, raceResult.channelMessage);

        if (workerQueue.length > 0) {
          yield* call(processCalculateAction, workerQueue.pop());
        } else {
          isWorkerBusy = false;
        }
      }
    } catch (e) {
      console.error(e);
    }
  }
}

export default pcaWorkerSaga;
